import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  addPending, getAllPending, updatePending, deletePending,
  addPairingPending, getPairingPending, updatePairingPending, deletePairingPending,
} from "@/lib/offlineQueue";

const MAX_RETRIES = 5;
const DRAIN_LOCK = "tenantseal-sync-drain";
const SyncContext = createContext(null);
export const useSync = () => useContext(SyncContext) || {};

// Distinguish a network-class failure (queue/retry) from a server rejection (surface as error).
export function isNetworkError(err) {
  if (!err) return false;
  if (err.response) return false; // HTTP 4xx/5xx = server rejection
  return Boolean(err.request) || err.code === "ERR_NETWORK" || err.message === "Network Error" || err.code === "ECONNABORTED";
}

// A request we aborted ourselves (e.g. on logout). Outcome is AMBIGUOUS — the server may or may not
// have accepted the bytes — so the persisted queue item must stay retryable with the SAME
// client_upload_id, letting server idempotency dedupe on the next resume.
export function isCanceled(err) {
  if (!err) return false;
  return err.code === "ERR_CANCELED" || err.name === "CanceledError" || err.name === "AbortError";
}

async function pingHealth() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 6000);
    const res = await fetch(`${API}/health`, { method: "GET", cache: "no-store", signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return false;
    const j = await res.json();
    return j?.status === "ok";
  } catch {
    return false;
  }
}

// Single upload pipeline (no second path): turn a queued record into a multipart POST.
async function postRecord(rec, signal) {
  const form = new FormData();
  form.append("file", new File([rec.image_blob], `${rec.client_upload_id}.jpg`, { type: rec.content_type || "image/jpeg" }));
  form.append("client_upload_id", rec.client_upload_id);
  if (rec.client_captured_at) form.append("client_captured_at", rec.client_captured_at);
  if (rec.import_mode) form.append("import_mode", rec.import_mode);
  if (rec.quality_flags) form.append("quality_flags", rec.quality_flags);
  if (rec.shot_slot) form.append("shot_slot", rec.shot_slot);
  if (rec.purpose) form.append("purpose", rec.purpose);
  form.append("offline_sync", "true");
  if (rec.latitude != null && rec.longitude != null) {
    form.append("client_latitude", rec.latitude);
    form.append("client_longitude", rec.longitude);
    if (rec.gps_accuracy != null) form.append("client_accuracy", rec.gps_accuracy);
  }
  // Feature 19: forward the frozen, opt-in device-location snapshot verbatim (owner-only provenance).
  // Reused unchanged on every retry so an idempotent resend keeps the original recorded snapshot.
  if (rec.device_location) {
    form.append("device_location", JSON.stringify(rec.device_location));
  }
  const { data } = await api.post(`/rooms/${rec.room_id}/photos`, form, { headers: { "Content-Type": "multipart/form-data" }, signal });
  return data;
}

export function SyncProvider({ children }) {
  const { user, logout } = useAuth();
  const ownerId = user?.user_id || null;
  const [pending, setPending] = useState([]);
  const [pairingPending, setPairingPending] = useState([]);
  const [memoryOnly, setMemoryOnly] = useState([]); // File held ONLY in memory (IDB persist failed)
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [justSynced, setJustSynced] = useState(false);
  const [syncTick, setSyncTick] = useState(0);
  const [logoutGuard, setLogoutGuard] = useState({ open: false, onProceed: null });
  const drainingRef = useRef(false);
  const ownerRef = useRef(ownerId);
  ownerRef.current = ownerId;
  const memRef = useRef([]);
  const activeAbortRef = useRef(null);   // AbortController for the in-flight upload (abort on logout)
  const workerStoppedRef = useRef(false); // set true to stop the queue worker before aborting

  const refresh = useCallback(async () => {
    setPending(ownerId ? await getAllPending(ownerId) : []);
    setPairingPending(ownerId ? await getPairingPending(ownerId) : []);
    if (!ownerId) { memRef.current = []; setMemoryOnly([]); }
  }, [ownerId]);

  const runDrain = useCallback(async () => {
    const uid = ownerRef.current;
    if (!uid) return;
    if (drainingRef.current) return;
    let queue = (await getAllPending(uid)).filter((r) => r.status !== "failed" && r.status !== "syncing");
    if (!queue.length) { await refresh(); return; }
    if (!(await pingHealth())) return;
    drainingRef.current = true;
    workerStoppedRef.current = false;
    setSyncing(true);
    const total = queue.length;
    let done = 0;
    setProgress({ done: 0, total });
    for (const rec of queue) {
      if (ownerRef.current !== uid || workerStoppedRef.current) break; // account switch / logout — stop now
      if ((rec.retry_count || 0) >= MAX_RETRIES) {
        await updatePending(rec.client_upload_id, { status: "failed", last_error: "Too many attempts" });
        await refresh();
        continue;
      }
      await updatePending(rec.client_upload_id, { status: "syncing" });
      await refresh();
      const ctrl = new AbortController();
      activeAbortRef.current = ctrl;
      try {
        await postRecord(rec, ctrl.signal);
        await deletePending(rec.client_upload_id);
        done += 1;
        setProgress({ done, total });
        setSyncTick((t) => t + 1);
        await refresh();
      } catch (err) {
        if (isCanceled(err)) {
          // We aborted (logout). Ambiguous outcome — keep the item pending with the SAME
          // client_upload_id so the next same-account resume dedupes rather than recreates.
          await updatePending(rec.client_upload_id, { status: "pending" });
          await refresh();
          break;
        }
        if (isNetworkError(err)) {
          await updatePending(rec.client_upload_id, { status: "pending", retry_count: (rec.retry_count || 0) + 1 });
          await refresh();
          break;
        }
        const msg = err?.response?.data?.detail;
        await updatePending(rec.client_upload_id, { status: "failed", last_error: typeof msg === "string" ? msg : "Upload rejected by server" });
        await refresh();
      } finally {
        activeAbortRef.current = null;
      }
    }
    setSyncing(false);
    drainingRef.current = false;
    const remaining = (await getAllPending(uid)).filter((r) => r.status !== "failed");
    if (remaining.length === 0 && done > 0) {
      setJustSynced(true);
      setTimeout(() => setJustSynced(false), 3000);
    }
  }, [refresh]);

  const drain = useCallback(async () => {
    if (!ownerRef.current) return;
    if (typeof navigator !== "undefined" && navigator.locks?.request) {
      return navigator.locks.request(DRAIN_LOCK, { ifAvailable: true }, async (lock) => {
        if (!lock) return; // another tab is draining — let it own the queue
        await runDrain();
      });
    }
    return runDrain(); // no Web Locks: server idempotency is the final duplicate guard
  }, [runDrain]);

  // ---------------- memory-only recovery ----------------
  const setMem = (next) => { memRef.current = next; setMemoryOnly(next); };
  const removeMem = useCallback((id) => {
    const rec = memRef.current.find((m) => m.client_upload_id === id);
    if (rec?._url) { try { URL.revokeObjectURL(rec._url); } catch { /* noop */ } }
    setMem(memRef.current.filter((m) => m.client_upload_id !== id));
  }, []);

  // Persist the EXACT accepted bytes locally BEFORE any network attempt, then try an immediate
  // upload. If local persistence is unavailable, we do NOT hard-block and do NOT claim it was
  // saved on device — the File stays in memory (memory-only recovery) until upload/save/discard.
  const captureAndSync = useCallback(async (record) => {
    const uid = ownerRef.current;
    const rec = { retry_count: 0, status: "pending", created_at: new Date().toISOString(), owner_user_id: uid, ...record };
    let persisted = false;
    try {
      await addPending(rec);
      persisted = true;
      await refresh();
    } catch (e) {
      console.warn("offline persistence unavailable", e);
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      if (!persisted) setMem([...memRef.current.filter((m) => m.client_upload_id !== rec.client_upload_id), { ...rec, reason: "offline_no_local_save" }]);
      return { queued: persisted, persisted, photo: null };
    }
    try {
      if (persisted) await updatePending(rec.client_upload_id, { status: "syncing" });
      const ctrl = new AbortController();
      activeAbortRef.current = ctrl;
      const data = await postRecord(rec, ctrl.signal);
      activeAbortRef.current = null;
      if (persisted) await deletePending(rec.client_upload_id);
      removeMem(rec.client_upload_id);
      setSyncTick((t) => t + 1);
      await refresh();
      return { queued: false, persisted, photo: data?.photo || data, already_uploaded: Boolean(data?.already_uploaded) };
    } catch (err) {
      activeAbortRef.current = null;
      if (isCanceled(err) || isNetworkError(err)) {
        // Aborted (logout) or network drop — ambiguous/retryable. Keep the item; never mark failed.
        if (persisted) { await updatePending(rec.client_upload_id, { status: "pending" }); await refresh(); }
        else setMem([...memRef.current.filter((m) => m.client_upload_id !== rec.client_upload_id), { ...rec, reason: "network_no_local_save" }]);
        return { queued: persisted, persisted, photo: null };
      }
      if (persisted) {
        const msg = err?.response?.data?.detail;
        await updatePending(rec.client_upload_id, { status: "failed", last_error: typeof msg === "string" ? msg : "Upload rejected by server" });
        await refresh();
      }
      throw err;
    }
  }, [refresh, removeMem]);

  const retrySaveLocal = useCallback(async (id) => {
    const rec = memRef.current.find((m) => m.client_upload_id === id);
    if (!rec) return { ok: false };
    try {
      await addPending({ ...rec, status: "pending", reason: undefined });
      removeMem(id);
      await refresh();
      if (typeof navigator !== "undefined" && navigator.onLine) drain();
      return { ok: true };
    } catch { return { ok: false }; }
  }, [refresh, drain, removeMem]);

  const retryUploadMemory = useCallback(async (id) => {
    const rec = memRef.current.find((m) => m.client_upload_id === id);
    if (!rec) return { ok: false };
    try {
      const data = await postRecord(rec);
      removeMem(id);
      setSyncTick((t) => t + 1);
      await refresh();
      return { ok: true, already_uploaded: Boolean(data?.already_uploaded) };
    } catch { return { ok: false }; }
  }, [refresh, removeMem]);

  const downloadBackup = useCallback((id) => {
    const rec = memRef.current.find((m) => m.client_upload_id === id);
    if (!rec) return;
    const url = URL.createObjectURL(rec.image_blob); // EXACT accepted bytes — no resize/recompress
    const a = document.createElement("a");
    a.href = url;
    a.download = (rec.filename || `tenantseal-${rec.client_upload_id}`) + (/\.[a-z0-9]+$/i.test(rec.filename || "") ? "" : ".jpg");
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }, []);

  const discardMemory = useCallback((id) => { removeMem(id); }, [removeMem]);

  // beforeunload guard — active ONLY while a memory-only copy is the sole known copy.
  useEffect(() => {
    if (!memoryOnly.length) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [memoryOnly.length]);

  // Browser Back / route-exit guard — active ONLY while a memory-only copy exists. beforeunload
  // covers refresh/tab-close/unload; this covers the SPA Back button, which beforeunload cannot.
  // We arm a single same-URL sentinel history entry (no accumulation, no loop): pressing Back fires
  // popstate → we confirm. Cancel re-arms the sentinel (stay); a deliberate leave removes the guard
  // and completes the Back navigation, honestly warning the memory-only capture will be lost.
  useEffect(() => {
    if (!memoryOnly.length) return undefined;
    window.history.pushState({ ddMemGuard: true }, "");
    const onPop = () => {
      const leave = window.confirm(
        "You have a photo saved only in this tab's memory. If you leave now it will be lost unless "
        + "you first download a backup or save/upload it. Leave anyway?");
      if (leave) {
        window.removeEventListener("popstate", onPop);
        window.history.back(); // proceed past the sentinel to the real previous page
      } else {
        window.history.pushState({ ddMemGuard: true }, ""); // stay — re-arm the single sentinel
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [memoryOnly.length]);

  // ---------------- Same-View pairing-pending ----------------
  const createPairingPending = useCallback(async (rec) => {
    const uid = ownerRef.current;
    const full = { id: rec.move_out_photo_id, owner_user_id: uid, status: "pending", created_at: new Date().toISOString(), ...rec };
    await addPairingPending(full);
    await refresh();
    return full;
  }, [refresh]);

  const confirmPairing = useCallback(async (rec) => {
    try {
      await api.post(`/properties/${rec.property_id}/same-view/pair`,
        { baseline_photo_id: rec.baseline_photo_id, move_out_photo_id: rec.move_out_photo_id, replace: true });
      await deletePairingPending(rec.id);
      await refresh();
      return { ok: true };
    } catch (err) {
      // Baseline no longer eligible (deleted/room mismatch) -> flag for attention, DO NOT auto-pick.
      const detail = err?.response?.data?.detail || "";
      const needsAttention = err?.response?.status === 400;
      await updatePairingPending(rec.id, { status: needsAttention ? "needs_attention" : "failed", last_error: typeof detail === "string" ? detail : "Pairing failed" });
      await refresh();
      return { ok: false, needsAttention };
    }
  }, [refresh]);

  const dismissPairing = useCallback(async (id) => {
    // Keep the record (discoverable later) — just mark it dismissed for this surface.
    await updatePairingPending(id, { dismissed: true });
    await refresh();
  }, [refresh]);

  const deletePairing = useCallback(async (id) => { await deletePairingPending(id); await refresh(); }, [refresh]);

  // ---------------- logout guard ----------------
  const hasPendingEvidence = useCallback(() => (
    pending.length > 0 || memoryOnly.length > 0 || pairingPending.length > 0 || syncing
  ), [pending.length, memoryOnly.length, pairingPending.length, syncing]);

  const requestLogout = useCallback((onProceed) => {
    if (!hasPendingEvidence()) { onProceed(); return; }
    setLogoutGuard({ open: true, onProceed });
  }, [hasPendingEvidence]);

  const cancelLogout = useCallback(() => setLogoutGuard({ open: false, onProceed: null }), []);

  const keepAndLogout = useCallback(async () => {
    // Stop the queue worker FIRST, then abort any in-flight request so its outcome is treated as
    // ambiguous (the persisted item stays pending). Keep never removes IndexedDB bytes.
    workerStoppedRef.current = true;
    try { activeAbortRef.current?.abort(); } catch { /* noop */ }
    const uid = ownerRef.current;
    if (uid) {
      const all = await getAllPending(uid);
      for (const r of all) if (r.status === "syncing") await updatePending(r.client_upload_id, { status: "pending" });
    }
    memRef.current = []; setMemoryOnly([]); // drop only the in-memory view; IDB records are preserved
    const cb = logoutGuard.onProceed;
    setLogoutGuard({ open: false, onProceed: null });
    await logout();
    if (cb) cb();
  }, [logout, logoutGuard]);

  const removeAndLogout = useCallback(async () => {
    // Stop the worker + abort in-flight before deleting local records (second-confirmation path).
    workerStoppedRef.current = true;
    try { activeAbortRef.current?.abort(); } catch { /* noop */ }
    const uid = ownerRef.current;
    if (uid) {
      const all = await getAllPending(uid);
      for (const r of all) await deletePending(r.client_upload_id); // current account's queued blobs only
      const pairs = await getPairingPending(uid);
      for (const p of pairs) await deletePairingPending(p.id);      // current account's pairing metadata (NOT server photos)
    }
    memRef.current.forEach((m) => { if (m._url) { try { URL.revokeObjectURL(m._url); } catch { /* noop */ } } });
    memRef.current = []; setMemoryOnly([]);
    const cb = logoutGuard.onProceed;
    setLogoutGuard({ open: false, onProceed: null });
    await logout();
    if (cb) cb();
  }, [logout, logoutGuard]);

  // Hydrate on load / account change and resume if online.
  useEffect(() => {
    (async () => {
      if (!ownerId) { setPending([]); setPairingPending([]); return; }
      const all = await getAllPending(ownerId);
      for (const r of all) if (r.status === "syncing") await updatePending(r.client_upload_id, { status: "pending" });
      await refresh();
      if (typeof navigator !== "undefined" && navigator.onLine) drain();
    })();
  }, [ownerId, refresh, drain]);

  useEffect(() => {
    const onOnline = () => { setIsOnline(true); drain(); };
    const onOffline = () => setIsOnline(false);
    const onVisible = () => { if (document.visibilityState === "visible") { setIsOnline(navigator.onLine); if (navigator.onLine) drain(); } };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [drain]);

  const enqueue = useCallback(async (record) => {
    const rec = { retry_count: 0, status: "pending", created_at: new Date().toISOString(), owner_user_id: ownerRef.current, ...record };
    await addPending(rec);
    await refresh();
    if (typeof navigator !== "undefined" && navigator.onLine) drain();
    return rec;
  }, [refresh, drain]);

  const retryFailed = useCallback(async () => {
    const uid = ownerRef.current;
    if (!uid) return;
    const all = await getAllPending(uid);
    for (const r of all) if (r.status === "failed") await updatePending(r.client_upload_id, { status: "pending", retry_count: 0, last_error: null });
    await refresh();
    drain();
  }, [refresh, drain]);

  const pendingByRoom = useCallback((roomId) => pending.filter((p) => p.room_id === roomId), [pending]);
  const pairingForProperty = useCallback((propertyId) => pairingPending.filter((p) => p.property_id === propertyId), [pairingPending]);

  const failedCount = pending.filter((p) => p.status === "failed").length;
  const queuedCount = pending.filter((p) => p.status !== "failed").length;

  return (
    <SyncContext.Provider value={{
      pending, pendingByRoom, enqueue, captureAndSync, retryFailed, refresh, drain,
      syncing, progress, isOnline, justSynced, syncTick,
      pendingCount: pending.length, failedCount, queuedCount,
      // memory-only recovery
      memoryOnly, retrySaveLocal, retryUploadMemory, downloadBackup, discardMemory,
      // pairing-pending
      pairingPending, pairingForProperty, createPairingPending, confirmPairing, dismissPairing, deletePairing,
      // logout guard
      logoutGuard, requestLogout, cancelLogout, keepAndLogout, removeAndLogout, hasPendingEvidence,
    }}>
      {children}
    </SyncContext.Provider>
  );
}
